// Identify / group helpers that skip PostHog calls whose properties haven't
// changed. posthog-js only dedupes `$set` in memory, so every full page load
// re-sent the same `$set` / `$groupidentify` — about 10 per session on www.
import posthog from 'posthog-js';

const STORAGE_PREFIX = 'attik_ph_sent:';

type Props = Record<string, string | number | boolean | null | undefined>;

function defined(props: Props): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/** True (and remembers `value`) when `value` differs from what was last sent under `key`. */
function changedSince(key: string, value: string): boolean {
  try {
    if (localStorage.getItem(STORAGE_PREFIX + key) === value) return false;
    localStorage.setItem(STORAGE_PREFIX + key, value);
  } catch {
    // storage blocked — fall back to sending
  }
  return true;
}

/** `posthog.identify`, sending person properties only when they changed. */
export function identifyPerson(distinctId: string, props: Props): void {
  const clean = defined(props);
  const sameUser = posthog.get_distinct_id() === distinctId;
  const changed = changedSince('person', JSON.stringify([distinctId, clean]));
  if (sameUser && !changed) {
    posthog.identify(distinctId);
    return;
  }
  posthog.identify(distinctId, clean);
}

/** `posthog.group`, sending group properties only when they changed. */
export function setGroup(groupType: string, groupKey: string, props?: Props): void {
  const clean = props ? defined(props) : undefined;
  const sameGroup = posthog.getGroups()[groupType] === groupKey;
  const changed =
    clean !== undefined &&
    changedSince(`group:${groupType}`, JSON.stringify([groupKey, clean]));
  posthog.group(groupType, groupKey, sameGroup && !changed ? undefined : clean);
}
